import { pageNumberState } from "@/atoms/order/page-number-state";
import { DEBOUNCE_TIME } from "@/common/constant/debounce-time";
import { initPage } from "@/common/constant/init-page";
import { PaginationSize } from "@/common/enum/pagination-size";
import { PartnerHeader } from "@/components/header/PartnerHeader";
import { PartnerListTable } from "@/components/table/partner-list-table/PartnerListTable";
import { userService } from "@/services/user-service";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { UserDto } from "@/types/dto/user-dto";
import { GetPartnerPagePayload } from "@/types/payload/get-partner-page-payload";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { PartnerOrder } from "../../common/enum/partner-order";
import { PartnerBar } from "../../components/bar/PartnerBar";
import { PartnerStatisticsTable } from "../../components/table/partner-statistics-table/PartnerStatisticsTable";
import { PageWrapper } from "../../components/wrapper/PageWrapper";

export default function PartnerListPage() {
  const [page, setPage] = useState<BasePageDto<UserDto>>(initPage);
  const [keyword, setKeyword] = useState<string>("");
  const [hasIncentiveDiscount, setHasIncentiveDiscount] =
    useState<boolean>(false);
  const [sortType, setSortType] = useState<PartnerOrder>(
    PartnerOrder.CREATED_AT
  );

  const getPage = async (input: GetPartnerPagePayload) => {
    try {
      const response = await userService.getPartnerPage(input);
      if (response.ok) {
        setPage(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const [pageNumber, setPageNumber] = useRecoilState(pageNumberState);

  const getPageDebounce = useCallback(
    debounce(async (input) => {
      await getPage(input);
    }, DEBOUNCE_TIME),
    []
  );

  useEffect(() => {
    const payload = {
      page: pageNumber,
      size: PaginationSize.SIZE_10,
      order: sortType,
      searchNames: keyword,
      hasIncentiveDiscount: hasIncentiveDiscount,
    };
    getPageDebounce(payload);
  }, [pageNumber]);

  useEffect(() => {
    const payload = {
      page: 1,
      size: PaginationSize.SIZE_10,
      order: sortType,
      searchNames: keyword,
      hasIncentiveDiscount: hasIncentiveDiscount,
    };
    getPageDebounce(payload);
    setPageNumber(1);
  }, [sortType, keyword, hasIncentiveDiscount]);

  const handlePageChange = (currentPage: number) => {
    setPage({ ...page, currentPage });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const handleSortChange = (sortType: PartnerOrder) => {
    setSortType(sortType);
  };

  const handleIncentiveDiscount = () => {
    setHasIncentiveDiscount(!hasIncentiveDiscount);
  };

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px] h-full items-between justify-start">
        <PartnerHeader />
        <div className="flex flex-col gap-[24px]">
          <PartnerStatisticsTable />
          <div>
            <PartnerBar
              totalElements={page.totalElements}
              keyword={keyword}
              sortType={sortType}
              hasIncentiveDiscount={hasIncentiveDiscount}
              handleSortChange={handleSortChange}
              handleSearchChange={handleSearchChange}
              handleIncentiveDiscount={handleIncentiveDiscount}
            />
            <PartnerListTable page={page} handlePageChange={handlePageChange} />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}
